/**
 * Boli Live - Audit Logs Page
 *
 * Admin page for viewing audit logs of admin actions.
 */

import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Shield,
  User,
  Ban,
  RefreshCw,
  Loader2,
  Clock,
  Filter,
} from 'lucide-react';
import { getApiUrl } from '@/lib/api-config';

interface AuditLog {
  id: string;
  adminId: string;
  adminName: string;
  action: string;
  targetType: string;
  targetId: string;
  details?: {
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
    reason?: string;
  };
  ipAddress?: string;
  created: string;
}

const actionLabels: Record<string, { label: string; color: string }> = {
  user_suspended: { label: 'User Suspended', color: 'text-red-600 bg-red-100' },
  user_unsuspended: { label: 'User Unsuspended', color: 'text-green-600 bg-green-100' },
  user_shadow_banned: { label: 'Shadow Ban Applied', color: 'text-orange-600 bg-orange-100' },
  user_shadow_unbanned: { label: 'Shadow Ban Removed', color: 'text-blue-600 bg-blue-100' },
  role_changed: { label: 'Role Changed', color: 'text-purple-600 bg-purple-100' },
  product_removed: { label: 'Product Removed', color: 'text-red-600 bg-red-100' },
  store_suspended: { label: 'Store Suspended', color: 'text-red-600 bg-red-100' },
  auction_cancelled: { label: 'Auction Cancelled', color: 'text-red-600 bg-red-100' },
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-PK', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('boli_auth_token');
      const params = new URLSearchParams();
      params.set('limit', limit.toString());
      params.set('offset', (page * limit).toString());
      if (actionFilter) params.set('action', actionFilter);

      const response = await fetch(getApiUrl(`/api/admin/audit-logs?${params}`), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.auditLogs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [actionFilter, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionIcon = (action: string) => {
    if (action.includes('suspended') || action.includes('banned') || action.includes('removed')) {
      return <Ban className="w-4 h-4" />;
    }
    if (action.includes('role')) {
      return <Shield className="w-4 h-4" />;
    }
    return <User className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link
                to="/admin"
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Audit Logs</h1>
                <p className="text-sm text-muted-foreground">{total} total entries</p>
              </div>
            </div>
            <button
              onClick={fetchLogs}
              className="p-2 hover:bg-secondary rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <select
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(0);
                }}
                className="px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
              >
                <option value="">All Actions</option>
                <option value="user_suspended">User Suspended</option>
                <option value="user_unsuspended">User Unsuspended</option>
                <option value="user_shadow_banned">Shadow Ban Applied</option>
                <option value="user_shadow_unbanned">Shadow Ban Removed</option>
                <option value="role_changed">Role Changed</option>
              </select>
            </div>
          </div>

          {/* Logs List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No audit logs found.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => {
                const actionInfo = actionLabels[log.action] || {
                  label: log.action,
                  color: 'text-gray-600 bg-gray-100',
                };

                return (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-lg border border-border p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${actionInfo.color}`}>
                          {getActionIcon(log.action)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${actionInfo.color}`}
                            >
                              {actionInfo.label}
                            </span>
                          </div>
                          <p className="text-sm text-foreground">
                            <span className="font-medium">{log.adminName || 'Admin'}</span>
                            {' performed action on '}
                            <span className="font-medium">{log.targetType}</span>
                            {' ID: '}
                            <code className="text-xs bg-secondary px-1 rounded">
                              {log.targetId}
                            </code>
                          </p>
                          {log.details?.reason && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Reason: {log.details.reason}
                            </p>
                          )}
                          {log.details?.after && (
                            <div className="text-xs text-muted-foreground mt-2 font-mono bg-secondary/50 rounded px-2 py-1">
                              Changes: {JSON.stringify(log.details.after)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatRelativeTime(log.created)}
                        </div>
                        {log.ipAddress && (
                          <p className="text-xs text-muted-foreground mt-1">
                            IP: {log.ipAddress}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {/* Pagination */}
              {total > limit && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of{' '}
                    {total}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                      className="px-3 py-1 border border-border rounded hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={(page + 1) * limit >= total}
                      className="px-3 py-1 border border-border rounded hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
